import React, { useState, useEffect } from 'react';
import {
  Form,
  Alert,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useGlobal } from '../../../Context/GlobalContext';
import { useWorkspace } from '../../../Context/WorkspaceContext';
import { FiShield } from 'react-icons/fi';
import { useFeaturePermissions } from '../../../hooks/useWorkspacePermissions';
import { RateLimitingSettingsSkeleton } from '../../../Components/SkeletonLoaders';
import PageHeader from '../../../Components/PageHeader';
import '../agent-settings.css';
import { useLanguage } from '../../../Context/LanguageContext';

const RateLimitingSettings = () => {
  const { id: chatbotId } = useParams();
  const { apiUrl } = useGlobal();
  const { fetchWorkspaceDetails, currentWorkspace } = useWorkspace();
  const { t } = useLanguage();

  // Get permission data
  const { agentSettings: settingsPermissions } = useFeaturePermissions();

  // Default values for Rate Limiting settings
  const defaultRateLimiting = {
    messagesPerMinute: 10,
    messagesPerHour: 50,
    messagesPerDay: 100,
    burstLimit: 5,
    sessionTimeoutMinutes: 30,
    ddosProtectionEnabled: true,
    adaptiveThrottling: false,
    blockDuration: 60, // minutes
  };

  // Settings state
  const [rateLimitSettings, setRateLimitSettings] = useState(defaultRateLimiting);
  const [lastSavedRateLimit, setLastSavedRateLimit] = useState(defaultRateLimiting);

  // Save and error state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Function to fetch rate limiting settings
  const fetchRateLimitSettings = async () => {
    setLoading(true);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(
        `${apiUrl}/api/v1/chatbot/get-chatbot-general-settings`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ chatbot_id: chatbotId })
        }
      );
      if (!response.ok) throw new Error(t('dashboard.settings.ratelimitingsettings.errorFetchSettings'));
      const data = await response.json();

      // Rate limit settings are now included in the general settings response
      const rateLimitData = data?.rate_limit_settings || {};
      const fetchedRateLimit = {
        ...defaultRateLimiting,
        ...rateLimitData,
      };
      setRateLimitSettings(fetchedRateLimit);
      setLastSavedRateLimit(fetchedRateLimit);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial loading: fetch rate limiting settings
  useEffect(() => {
    fetchRateLimitSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, chatbotId]);

  // Helper function to check if settings have changed
  const isRateLimitChanged = () =>
    JSON.stringify(rateLimitSettings) !== JSON.stringify(lastSavedRateLimit);

  // Helper function to check if there are validation errors
  const hasValidationErrors = () => Object.keys(fieldErrors).length > 0;

  // Save function for Rate Limiting Settings
  const handleSaveRateLimit = async () => {
    setIsSaving(true);
    setError(null);
    setFieldErrors({});

    // Validate settings before sending to API
    const validationErrors = validateAllRateLimitSettings(rateLimitSettings);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError(t('dashboard.settings.ratelimitingsettings.validationErrorCorrect'));
      setIsSaving(false);
      return;
    }

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Prepare request body according to new schema
      const requestBody = {
        chatbot_id: chatbotId,
        messagesPerMinute: rateLimitSettings.messagesPerMinute,
        messagesPerHour: rateLimitSettings.messagesPerHour,
        messagesPerDay: rateLimitSettings.messagesPerDay,
        burstLimit: rateLimitSettings.burstLimit,
        blockDuration: rateLimitSettings.blockDuration,
      };

      const response = await fetch(`${apiUrl}/api/v1/chatbot/update-rate-limit-settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json();

        // Handle different error response formats
        if (data.detail && typeof data.detail === 'object') {
          // Backend returned structured error with field_errors
          if (data.detail.field_errors) {
            setFieldErrors(data.detail.field_errors);
            setError(data.detail.message || t('dashboard.settings.ratelimitingsettings.validationErrorHighlight'));
          } else {
            setError(data.detail.message || t('dashboard.settings.ratelimitingsettings.errorUpdateSettings'));
          }
        } else if (data.field_errors) {
          // Direct field errors format
          setFieldErrors(data.field_errors);
          setError(data.message || data.error || t('dashboard.settings.ratelimitingsettings.validationErrorHighlight'));
        } else {
          // Simple error message
          setError(data.message || data.detail || t('dashboard.settings.ratelimitingsettings.errorUpdateSettings'));
        }
        return;
      }

      setLastSavedRateLimit(rateLimitSettings);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
      await fetchWorkspaceDetails(currentWorkspace.workspace_id, true); // Force refresh
    }
  };

  const handleResetRateLimit = () => {
    setRateLimitSettings(lastSavedRateLimit);
    setFieldErrors({});
  };

  // Validation functions
  const validateRateLimitField = (field, value) => {
    const boundaries = {
      messagesPerMinute: { min: 1, max: 100, labelKey: "fieldLabelMessagesPerMinute" },
      messagesPerHour: { min: 10, max: 200, labelKey: "fieldLabelMessagesPerHour" },
      messagesPerDay: { min: 50, max: 500, labelKey: "fieldLabelMessagesPerDay" },
      burstLimit: { min: 1, max: 20, labelKey: "fieldLabelBurstLimit" },
      blockDuration: { min: 1, max: 1440, labelKey: "fieldLabelBlockDuration" }
    };

    const boundary = boundaries[field];
    if (!boundary) return null;

    const label = t(`dashboard.settings.ratelimitingsettings.${boundary.labelKey}`);

    if (value < boundary.min) {
      return t('dashboard.settings.ratelimitingsettings.validationMinValue', { label, min: boundary.min });
    }
    if (value > boundary.max) {
      return t('dashboard.settings.ratelimitingsettings.validationMaxValue', { label, max: boundary.max });
    }
    return null;
  };

  const validateLogicalRelationships = (settings) => {
    const errors = {};

    // Burst limit should not exceed messages per minute
    if (settings.burstLimit > settings.messagesPerMinute) {
      errors.burstLimit = t('dashboard.settings.ratelimitingsettings.validationBurstExceedsMinute', {
        burst: settings.burstLimit,
        minute: settings.messagesPerMinute
      });
    }

    // Messages per hour should be at least equal to messages per minute
    if (settings.messagesPerHour < settings.messagesPerMinute) {
      errors.messagesPerHour = t('dashboard.settings.ratelimitingsettings.validationHourBelowMinute', {
        hour: settings.messagesPerHour,
        minute: settings.messagesPerMinute
      });
    }

    // Messages per day should be at least equal to messages per hour
    if (settings.messagesPerDay < settings.messagesPerHour) {
      errors.messagesPerDay = t('dashboard.settings.ratelimitingsettings.validationDayBelowHour', {
        day: settings.messagesPerDay,
        hour: settings.messagesPerHour
      });
    }

    return errors;
  };

  const validateAllRateLimitSettings = (settings) => {
    const errors = {};

    // Validate individual field boundaries
    Object.keys(settings).forEach(field => {
      const error = validateRateLimitField(field, settings[field]);
      if (error) {
        errors[field] = error;
      }
    });

    // Only validate logical relationships if no boundary errors
    if (Object.keys(errors).length === 0) {
      const logicalErrors = validateLogicalRelationships(settings);
      Object.assign(errors, logicalErrors);
    }

    return errors;
  };

  // Clear field errors when user starts typing and validate in real-time
  const handleRateLimitChange = (field, value) => {
    const newSettings = { ...rateLimitSettings, [field]: value };
    setRateLimitSettings(newSettings);

    // Start with empty errors
    const newFieldErrors = {};

    // Validate ALL fields for boundary errors with new settings
    Object.keys(newSettings).forEach(key => {
      const error = validateRateLimitField(key, newSettings[key]);
      if (error) {
        newFieldErrors[key] = error;
      }
    });

    // If no boundary errors, validate logical relationships
    if (Object.keys(newFieldErrors).length === 0) {
      const logicalErrors = validateLogicalRelationships(newSettings);
      Object.assign(newFieldErrors, logicalErrors);
    }

    setFieldErrors(newFieldErrors);
  };

  // Create PageHeader component to be used in both skeleton and main render
  const pageHeaderComponent = (
    <PageHeader
      icon={<FiShield />}
      title={t('dashboard.settings.ratelimitingsettings.pageTitle')}
      description={t('dashboard.settings.ratelimitingsettings.pageDescription')}
      hideDescriptionOnMobile={false}
      cancelButton={settingsPermissions.canModify ? {
        children: t('dashboard.settings.ratelimitingsettings.resetButton'),
        onClick: handleResetRateLimit,
        disabled: isSaving || !isRateLimitChanged(),
        variant: "outline-secondary"
      } : null}
      saveButton={settingsPermissions.canModify ? {
        children: isSaving ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            {t('dashboard.settings.ratelimitingsettings.savingText')}
          </>
        ) : hasValidationErrors() ? (
          t('dashboard.settings.ratelimitingsettings.fixErrorsButton')
        ) : isRateLimitChanged() ? (
          t('dashboard.settings.ratelimitingsettings.saveButton')
        ) : (
          t('dashboard.settings.ratelimitingsettings.savedButton')
        ),
        onClick: handleSaveRateLimit,
        disabled: isSaving || !isRateLimitChanged() || hasValidationErrors(),
        variant: "primary"
      } : null}
    />
  );

  if (loading) {
    return <RateLimitingSettingsSkeleton pageHeader={pageHeaderComponent} />;
  }

  return (
    <div className="agent-settings-container">
      {pageHeaderComponent}

      <div className="settings-content">
        {/* Display save/fetch errors */}
        {error && (
          <Alert variant="danger" className="settings-alert">
            {error}
          </Alert>
        )}

        <div className="settings-form-container">
          <Form>

            <div className="settings-form-grid">
              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.ratelimitingsettings.messagesPerMinuteLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="100"
                      value={rateLimitSettings.messagesPerMinute}
                      onChange={(e) => handleRateLimitChange('messagesPerMinute', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.messagesPerMinute ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.ratelimitingsettings.messagesPerMinuteUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.messagesPerMinute && (
                  <div className="settings-error-text">
                    {fieldErrors.messagesPerMinute}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.ratelimitingsettings.messagesPerMinuteHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.ratelimitingsettings.burstLimitLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="20"
                      value={rateLimitSettings.burstLimit}
                      onChange={(e) => handleRateLimitChange('burstLimit', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.burstLimit ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.ratelimitingsettings.burstLimitUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.burstLimit && (
                  <div className="settings-error-text">
                    {fieldErrors.burstLimit}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.ratelimitingsettings.burstLimitHelp')}
                </Form.Text>
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.ratelimitingsettings.messagesPerHourLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="10"
                      max="200"
                      value={rateLimitSettings.messagesPerHour}
                      onChange={(e) => handleRateLimitChange('messagesPerHour', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.messagesPerHour ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.ratelimitingsettings.messagesPerHourUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.messagesPerHour && (
                  <div className="settings-error-text">
                    {fieldErrors.messagesPerHour}
                  </div>
                )}
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.ratelimitingsettings.messagesPerDayLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="50"
                      max="500"
                      value={rateLimitSettings.messagesPerDay}
                      onChange={(e) => handleRateLimitChange('messagesPerDay', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.messagesPerDay ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.ratelimitingsettings.messagesPerDayUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.messagesPerDay && (
                  <div className="settings-error-text">
                    {fieldErrors.messagesPerDay}
                  </div>
                )}
              </div>

              <div className="settings-form-group">
                <Form.Label className="settings-label">{t('dashboard.settings.ratelimitingsettings.blockDurationLabel')}</Form.Label>
                <div className={`settings-input-wrapper ${!settingsPermissions.canModifyRateLimit ? 'permission-restricted' : ''}`}>
                  <InputGroup className="settings-input-group">
                    <Form.Control
                      type="number"
                      min="1"
                      max="1440"
                      value={rateLimitSettings.blockDuration}
                      onChange={(e) => handleRateLimitChange('blockDuration', parseInt(e.target.value))}
                      className={`settings-input ${fieldErrors.blockDuration ? 'is-invalid' : ''}`}
                      disabled={!settingsPermissions.canModifyRateLimit}
                      readOnly={!settingsPermissions.canModifyRateLimit}
                    />
                    <InputGroup.Text className="settings-input-addon">{t('dashboard.settings.ratelimitingsettings.blockDurationUnit')}</InputGroup.Text>
                  </InputGroup>
                </div>
                {fieldErrors.blockDuration && (
                  <div className="settings-error-text">
                    {fieldErrors.blockDuration}
                  </div>
                )}
                <Form.Text className="settings-help-text">
                  {t('dashboard.settings.ratelimitingsettings.blockDurationHelp')}
                </Form.Text>
              </div>
            </div>

          </Form>

        </div>

        {showSuccess && (
          <Alert variant="success" className="settings-success-alert">
            {t('dashboard.settings.ratelimitingsettings.successSaved')}
          </Alert>
        )}
      </div>
    </div>
  );
};

export default RateLimitingSettings;